import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { faqList, siteConfig, verifyRules } from "@/mocks/site";

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [status, setStatus] = useState<"idle" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 · 求证方式与常见疑问 · 校园爆料";
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("phone_alt") as string) || "";
    setStatus("idle");
    setFormError("");

    if (honeypot.trim()) {
      setStatus("error");
      setFormError("提交失败，请稍后重试。");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "phone_alt") return;
      payload.append(key, String(value));
    });

    try {
      const res = await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code as string | undefined;
      const message = (parsed?.meta as Record<string, unknown>)?.message as string | undefined;
      const detail = (parsed?.meta as Record<string, unknown>)?.detail as string | undefined;
      const lower = `${message || ""} ${detail || ""} ${responseText}`.toLowerCase();
      if (res.ok && code === "OK" && !lower.includes("spam")) {
        setStatus("idle");
        form.reset();
        alert("感谢提交，我们会尽快回复你的线索与留言。");
      } else {
        setStatus("error");
        setFormError(message || detail || "提交未成功，请检查后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎出了点问题，请稍后再试。");
    }
  };

  return (
    <>
      {/* 页头 */}
      <section className="relative flex items-center overflow-hidden bg-[#191410] pb-12 pt-20">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=an%20old%20worn%20wooden%20desk%20with%20open%20old%20books%20a%20quill%20and%20a%20dim%20oil%20lamp%20in%20a%20dark%20study%2C%20warm%20amber%20light%2C%20ink%20wash%20editorial%20illustration%2C%20deep%20brown%20black%20tones&width=1920&height=700&seq=about-banner-04&orientation=landscape"
            alt="校园爆料求证方式背景"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
          <nav className="mb-4 text-sm text-parchment-300/60">首页 <i className="ri-arrow-right-s-line align-middle"></i> 关于我们</nav>
          <h1 className="serif-head text-4xl font-black text-parchment-100 md:text-5xl">我们如何求证一个校园怪谈</h1>
          <p className="serif-head mt-4 max-w-2xl text-lg leading-relaxed text-parchment-300/85">
            你好，我们是「校园爆料」的编辑小组。专注求证校园怪谈是真的吗，梳理学校未解传闻，抢救并还原校园旧事档案中的真相。
          </p>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="bg-night-900 py-14">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 md:px-6 md:grid-cols-3">
          <div className="rounded-lg border border-night-600/50 bg-night-850 p-7">
            <i className="ri-compass-3-line text-3xl text-amberlight-400"></i>
            <h2 className="serif-head mt-4 text-xl font-bold text-parchment-100">网站定位</h2>
            <p className="mt-3 text-sm leading-relaxed text-parchment-300/75">
              面向所有对校园怪谈与校园历史感兴趣的读者，把口耳相传的传闻转化为可追溯、可核对的文字档案，做传说与事实之间的桥梁。
            </p>
          </div>
          <div className="rounded-lg border border-night-600/50 bg-night-850 p-7">
            <i className="ri-vip-diamond-line text-3xl text-amberlight-400"></i>
            <h2 className="serif-head mt-4 text-xl font-bold text-parchment-100">发展理念</h2>
            <p className="mt-3 text-sm leading-relaxed text-parchment-300/75">
              我们相信，任何一段吓过人的校园传说，背后都站着一群真实的人和一段真实的旧时光。求真，也求对记忆的尊重。
            </p>
          </div>
          <div className="rounded-lg border border-night-600/50 bg-night-850 p-7">
            <i className="ri-book-2-line text-3xl text-amberlight-400"></i>
            <h2 className="serif-head mt-4 text-xl font-bold text-parchment-100">内容原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-parchment-300/75">
              人物化名、来源标注、结论分级、证据不足即存疑。不渲染恐怖，不消费在校师生，不虚构事实与荣誉。
            </p>
          </div>
        </div>
      </section>

      {/* 求证流程 */}
      <section className="border-t border-night-700/40 bg-[#191410] py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-10 text-center">
            <span className="hand-script text-2xl text-amberlight-400">求证方法论</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">一套档案，四个求证步骤</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {verifyRules.map((r, i) => (
              <div key={r.step} className="rounded-lg border border-night-600/50 bg-night-850 p-6">
                <span className="serif-head text-4xl font-black text-cinnabar-500">{r.step}</span>
                <h3 className="serif-head mt-3 text-lg font-bold text-parchment-100">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-parchment-300/75">{r.desc}</p>
                <span className="mt-4 block text-xs text-parchment-300/50">第 {i + 1} 步</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑与常见疑问 */}
      <section id="faq" className="bg-night-900 py-14">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 md:px-6 lg:grid-cols-2">
          <div>
            <span className="hand-script text-2xl text-amberlight-400">读者怎么看</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">我们收到的口碑</h2>
            <div className="mt-6 space-y-4">
              {[
                { who: "@南方的风（校友）", text: "看完旧琴房那篇很感慨。求证没有泼冷水，反而让那段集体记忆更完整了。" },
                { who: "@阿篱（在校生）", text: "关于图书馆的灯那篇，原来真的是值夜的老管理员。以后经过会多看一眼那扇窗。" },
                { who: "@小满（读者）", text: "很多怪谈终于有了一条清晰的求证路径，不再是一味吓人或一味反驳，很难得。" },
              ].map((t) => (
                <figure key={t.who} className="rounded-lg border border-night-600/50 bg-night-850 p-5">
                  <i className="ri-double-quotes-l text-2xl text-cinnabar-500/50"></i>
                  <blockquote className="mt-2 text-sm leading-relaxed text-parchment-300/85">{t.text}</blockquote>
                  <figcaption className="mt-3 text-xs text-parchment-300/60">{t.who}</figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div>
            <span className="hand-script text-2xl text-amberlight-400">你问的正是我们求证的</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">常见疑问</h2>
            <div className="mt-6 space-y-3">
              {faqList.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="overflow-hidden rounded-lg border border-night-600/50 bg-night-850">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="serif-head text-base font-semibold text-parchment-100">{f.q}</span>
                      <i className={`shrink-0 text-amberlight-400 transition-transform ${open ? "ri-subtract-line" : "ri-add-line"}`}></i>
                    </button>
                    {open && (
                      <div className="border-t border-night-700/50 px-5 py-4 text-sm leading-relaxed text-parchment-300/80">{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 联系 / 投稿表单 */}
      <section className="border-t border-night-700/40 bg-[#191410] py-14">
        <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 md:px-6 lg:grid-cols-2">
          <div>
            <span className="hand-script text-2xl text-amberlight-400">联系我们</span>
            <h2 className="serif-head mt-1 text-3xl font-bold text-parchment-100">投稿你的校园传说</h2>
            <p className="mt-4 leading-relaxed text-parchment-300/80">
              你听过一则有趣的校园怪谈、学校未解传闻，或有一段亲历的旧事想求证？欢迎把线索发给我们。请尽量写明发生年代、学校类型与大概地区、完整经过与消息来源。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-parchment-300/75">
              <li className="flex items-center gap-2"><i className="ri-mail-line text-amberlight-400"></i>{siteConfig.contactEmail}</li>
              <li className="flex items-center gap-2"><i className="ri-lock-line text-amberlight-400"></i>所有来稿默认匿名化处理</li>
              <li className="flex items-center gap-2"><i className="ri-eye-off-line text-amberlight-400"></i>在校学生敏感线索绝不公开指向</li>
            </ul>
            <Link
              to="/archive"
              className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-night-500/60 px-5 py-2.5 text-sm text-parchment-200 transition hover:border-amberlight-400/60 hover:text-amberlight-300"
            >
              先看看别人投稿的档案
            </Link>
          </div>

          <form
            data-readdy-form="校园爆料投稿联系"
            action="https://readdy.ai/api/form/dagm1t1tto0ceqqnhvd0"
            method="POST"
            onSubmit={handleSubmit}
            className="rounded-lg border border-night-600/50 bg-night-850 p-7"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1.5 block text-parchment-300/80">你的称呼</span>
                <input name="name" type="text" required className="w-full rounded-md border border-night-600/60 bg-night-800 px-3 py-2.5 text-sm text-parchment-100 placeholder:text-parchment-300/40 focus:border-amberlight-400/60 focus:outline-none" placeholder="匿名亦可" />
              </label>
              <label className="block text-sm">
                <span className="mb-1.5 block text-parchment-300/80">你的邮箱</span>
                <input name="email" type="email" required className="w-full rounded-md border border-night-600/60 bg-night-800 px-3 py-2.5 text-sm text-parchment-100 placeholder:text-parchment-300/40 focus:border-amberlight-400/60 focus:outline-none" placeholder="用于回复你" />
              </label>
            </div>
            <label className="mt-4 block text-sm">
              <span className="mb-1.5 block text-parchment-300/80">传闻主题 / 学校类型（选填）</span>
              <input name="topic" type="text" className="w-full rounded-md border border-night-600/60 bg-night-800 px-3 py-2.5 text-sm text-parchment-100 placeholder:text-parchment-300/40 focus:border-amberlight-400/60 focus:outline-none" placeholder="例如：宿舍楼道传闻 / 某师范大学" />
            </label>
            <label className="mt-4 block text-sm">
              <span className="mb-1.5 block text-parchment-300/80">你的校园传说 / 求证线索</span>
              <textarea name="message" rows={5} maxLength={500} required className="w-full resize-none rounded-md border border-night-600/60 bg-night-800 px-3 py-2.5 text-sm leading-relaxed text-parchment-100 placeholder:text-parchment-300/40 focus:border-amberlight-400/60 focus:outline-none" placeholder="请尽量写出完整经过、来源与大致年代（500 字以内）"></textarea>
            </label>
            <div className="honeypot-field" aria-hidden="true">
              <label>请勿填写此栏</label>
              <input name="phone_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
            </div>
            <button
              type="submit"
              className="mt-5 w-full whitespace-nowrap rounded-md bg-cinnabar-600 px-6 py-3 text-parchment-100 transition hover:bg-cinnabar-500"
            >
              提交线索
            </button>
            {status === "error" && (
              <p className="mt-3 rounded-md border border-cinnabar-500/40 bg-cinnabar-600/10 px-4 py-2.5 text-sm text-cinnabar-300">
                {formError}
              </p>
            )}
            <p className="mt-3 text-center text-xs text-parchment-300/50">提交即表示同意我们匿名化处理你的线索。</p>
          </form>
        </div>
      </section>
    </>
  );
}